import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, Hourglass, ShieldAlert } from 'lucide-react';
import MetricsGrid from '@components/common/MetricsGrid';
import MetricCard from '@components/common/MetricCard';
import { CONTRACT_ROLE_METRIC_ROUTES } from '@utils/contractRoleDashboardRoutes';
import type { ContractRoleMetricsData } from '@app-types';
import { DashboardSectionError } from './DashboardSectionError';

interface ContractRoleMetricsProps {
  data: ContractRoleMetricsData | undefined;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
}

const ContractRoleMetrics: React.FC<ContractRoleMetricsProps> = ({ data, isLoading, isError, onRetry }) => {
  const navigate = useNavigate();

  if (isError) {
    return (
      <DashboardSectionError
        message="Failed to load assessment metrics."
        onRetry={onRetry}
        dataTestId="contract-role-metrics-error"
      />
    );
  }

  return (
    <MetricsGrid>
      <MetricCard
        icon={<Hourglass size={24} />}
        label="Assessments Pending Evidence"
        value={data?.assessments_pending_evidence ?? 0}
        subtitle="Waiting for your evidence"
        variant="orange"
        loading={isLoading}
        onClick={() => navigate(CONTRACT_ROLE_METRIC_ROUTES.assessmentsPendingEvidence)}
      />
      <MetricCard
        icon={<AlertCircle size={24} />}
        label="Assessments Past Due"
        value={data?.assessments_past_due ?? 0}
        subtitle="Evidence deadline has passed"
        variant="red"
        loading={isLoading}
        onClick={() => navigate(CONTRACT_ROLE_METRIC_ROUTES.assessmentsPastDue)}
      />
      <MetricCard
        icon={<ShieldAlert size={24} />}
        label="Assessments Escalated"
        value={data?.assessments_escalated ?? 0}
        subtitle="Escalated for overdue evidence"
        variant="red"
        loading={isLoading}
        onClick={() => navigate(CONTRACT_ROLE_METRIC_ROUTES.assessmentsEscalated)}
      />
    </MetricsGrid>
  );
};

export { ContractRoleMetrics };
